import React from 'react';
import * as Sentry from '@sentry/browser';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import FormHelperText from "@material-ui/core/FormHelperText/FormHelperText";
import { withStyles } from '@material-ui/core/styles';
import { Link, Redirect } from "react-router-dom";
import { TextValidator, ValidatorForm } from 'react-material-ui-form-validator';

import googleLogo from '../google_g.svg';
import { styles } from '../styles';
import { parametersFromCookiesAndQuery, redirectToGoogleSSO } from "../utils";
import { requestPubnubApiPOST, withCredentials } from "../apiClient";
import ErrorSnackbar from "./ErrorSnackbar";
import CircularProgress from '@material-ui/core/CircularProgress/CircularProgress';
import { getRecaptchaToken as getGoogleRecaptchaToken, onRecaptchaReady as onGoogleRecaptchaReady } from "./grecaptchaHelpers";

const TermsAndCondsAgreement = withStyles(styles)(
  props => (
      <div>
        <FormHelperText className={props.classes.formHelperText}>
          By clicking on Sign Up, or Register with Gmail, you agree to our{' '}
          <a
            href="https://www.pubnub.com/terms-and-conditions/"
            target="_blank"
            className={props.classes.link}
            rel="noopener noreferrer"
          >Terms & Conditions</a>
          {' '}and{' '}
          <a
            href="https://www.pubnub.com/privacy-policy/"
            target="_blank"
            className={props.classes.link}
            rel="noopener noreferrer"
          >Privacy Policy</a>.
        </FormHelperText>
        <FormHelperText className={props.classes.formHelperText}>
          This site is protected by reCAPTCHA and the Google{' '}
          <a
            className={props.classes.link}
            rel="noopener noreferrer"
            href="https://policies.google.com/privacy"
          >
            Privacy Policy
          </a>{' '}
          and{' '}
          <a
            className={props.classes.link}
            rel="noopener noreferrer"
            href="https://policies.google.com/terms"
          >
          Terms of Service
          </a>{' '}
          apply.
        </FormHelperText>
      </div>
  )
);

class Signup extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      submitErrorText: '',
      showErrorSnackbar: false,
      disabled: false,
    };
  }

  componentDidMount = () => {
    ValidatorForm.addValidationRule('pubnubPassword', password => {
      return (password && !(
        (password.length < 8) ||
        (password.length > 72) ||
        !/[A-Z]/.test(password) ||
        !/[a-z]/.test(password) ||
        !/[0-9]/.test(password) ||
        !(/[^\w]/.test(password) || /[_]/.test(password))
      ));
    });
  };

  createUser = (token) => {
    this.setState({ disabled: true });
    return withCredentials(requestPubnubApiPOST)(
      '/users/quickstart',
      {
        body: {
          properties: parametersFromCookiesAndQuery(),
          ...this.props.credentials,
          'g-recaptcha-response': token,
          'enterprise': true,
          ...(process.env.NODE_ENV !== 'production' ? { 'g-recaptcha-response': false } : '')
        }
      }
    ).then(response => {
      const { result } = response.body;
      if (result.reason && result.reason === 'UNVERIFIED_EMAIL') {
        this.props.history.push('/confirm-email')
      } else {
        Sentry.captureException(`Unhandled reason: ${result.reason}`, )
      }
      this.setState({ disabled: false });
    }).catch(error => {
      if(error.response) {
        let errorText = error.response.body.error;
        if (errorText === 'Sorry, your account cannot be created. It seems that your account already exists. Can we help you recover your password?') {
          errorText = 'Sorry, your account cannot be created. It seems that your account already exists.'
        }
        this.setState({
          submitErrorText: errorText,
          showErrorSnackbar: true,
        });
      } else {
        this.setState({
          submitErrorText: 'Something unexpected happen, please try again later',
          showErrorSnackbar: true,
        });
      }
      this.setState({ disabled: false });
    })
  };

  handleSubmit = () => {
    this.props.onRecaptchaReady(async () => {
      const token = await this.props.getRecaptchaToken('6LdxhW8pAAAAAArRGDvcAZBXwP5rJfx0zwRGzbCi', {action: 'LOGIN'});

      return this.createUser(token);
    });
  };

  handleErrors = errors => {
    // console.log(errors)
  };

  closeSnackbar = () => {
    this.setState({
      ...this.state,
      showErrorSnackbar: false,
    })
  };

  render() {
    if(this.props.loggedIn){
      return <Redirect to={'/activate-pubsub'} />
    }
    return (
      <div className="App" style={{margin: "0 auto"}}>
        <Grid container spacing={8}>
          <Grid item xs>
            <img src="pubnub.png" alt="PubNub" style={{ width: "100px" }}/>
          </Grid>
          <Grid item xs={5}>
            <Typography variant="title" gutterBottom className={this.props.classes.title}>
              Sign Up
            </Typography>
            <div>
              <Button onClick={redirectToGoogleSSO} variant="contained" className={this.props.classes.loginWithGoogle}>
                <img alt="Google G logo" src={googleLogo} className={this.props.classes.googleLogo}/>
                Register with Gmail
              </Button>
            </div>
            <div className={this.props.classes.divider}>
              Or Sign Up with Email
            </div>
            <div>
              <ValidatorForm
                ref="form"
                onSubmit={this.handleSubmit}
                onError={this.handleErrors}
              >
                <TextValidator
                  name="email"
                  id="email-input"
                  label="Email Address"
                  value={this.props.credentials.email}
                  onChange={this.props.handleChange('email')}
                  fullWidth
                  className={this.props.classes.textfield}
                  validators={['required', 'isEmail']}
                  errorMessages={['this field is required', 'email is not valid']}
                  disabled={this.state.disabled}
                />
                <TextValidator
                  name="password"
                  id="password-input"
                  label="Password"
                  type="password"
                  value={this.props.credentials.password}
                  onChange={this.props.handleChange('password')}
                  fullWidth
                  helperText="At least 8 characters, contain one uppercase, one lowercase, one number and one special character."
                  className={this.props.classes.textfield}
                  validators={['required', 'pubnubPassword']}
                  errorMessages={['this field is required', 'At least 8 characters, contain one uppercase, one lowercase, one number and one special character.']}
                  disabled={this.state.disabled}
                />
                <Button
                  id="singupButton"
                  type="submit"
                  variant="contained"
                  color="primary"
                  className={this.props.classes.button}
                  disabled={this.state.disabled}
                >
                  {this.state.disabled ?
                    <CircularProgress size={21}
                                      className={this.props.classes.buttonCircularProgress}
                    /> : 'Sign Up for Free'}
                </Button>
                <TermsAndCondsAgreement />
              </ValidatorForm>
            </div>
          </Grid>
          <Grid item xs>
            Already have an account?&nbsp;
            <Link to={`/login${window.location.search}`} className={this.props.classes.link}>Login</Link>
          </Grid>
        </Grid>
        <ErrorSnackbar
          message={this.state.submitErrorText}
          open={this.state.showErrorSnackbar}
          onClose={this.closeSnackbar}
        />
      </div>
    )
  }
}

Signup.defaultProps = {
  onRecaptchaReady:onGoogleRecaptchaReady,
  getRecaptchaToken: getGoogleRecaptchaToken
};
export default withStyles(styles)(Signup);



// WEBPACK FOOTER //
// ./src/componentes/Signup.js